// One pass over the container: what is in it, and what is inside that.
//
// This is phase 2 of a scan (see `analyzeJar`). It was ~780 lines of the
// middle of a 1,400-line function, and it is the phase the last audit called
// the boundary that still looked wrong. Extracting it was worth doing for one
// reason more than the length: **the walk is the only producer of almost every
// fact the rest of a scan knows.** Classes, strings, dependencies, entry
// points, nested archives, decoded values, the entry hashes and the sample
// hashes are all decided here. Once that is behind one function, the remaining
// phases read as consumers rather than as co-accumulators.
//
// The walk is deliberately *not* split further into hash / index / parse /
// nested-archive steps. Those steps share about twenty accumulators — `classes`,
// `extractedStrings`, `contexts`, `staticRecoveryBySource`, the instruction
// budget, the nested-archive budget — and every one of them is mutated by more
// than one step. Separating them would mean passing a mutable bag between four
// functions, which is the coupling this extraction exists to remove, not
// reintroduce one level down. The steps are named in comments instead.
//
// Two pieces of state deliberately cross the boundary to the detector phase:
// the entry-bytes cache (`hashedEntryBytes`), because the payload and YARA
// detectors read entries the walk already decompressed, and the string-id
// counter, because string indexing continues into the decoder. Both are
// documented on `WalkResult`.

import {
  archiveLayoutDigest,
  classifyEntry,
  classifyZipObject,
  digest,
  extractBinaryStrings,
  MAX_ENTRIES,
  parseManifest,
  parseProperties,
  zipSize,
} from "./archive-ingest";
import { parseClassFile } from "./classfile";
import JSZip from "jszip";
import { recoverConstantStringExpressions } from "./constant-string-evaluator";
import { matchSuspiciousWords } from "./detection-dictionary";
import type { AnalyzeOptions } from "./analyzer-options";
import {
  decodeCharacterPackedString,
  decodeXorPackedString,
  recoverStaticStrings,
  usefulReassembledValue,
} from "./static-recovery";
import {
  decodeBase64,
  decodeHex,
  staticRecoveredTransform,
} from "./static-recovery";
import {
  lookupOfficialModrinthFile,
  skippedOfficialLookup,
} from "./official-reputation";
import { ensureNotAborted, nextFrame, reportProgress } from "./scan-runtime";
import { matchKnownHashes } from "./threat-intel";
import type { DetectionContext } from "./detection-rules";
import type {
  ArchiveEntry,
  OfficialFileMatch,
  ClassInfo,
  Dependency,
  EntryHash,
  EntryPoint,
  ExtractedString,
  KnownHashMatch,
  RecoveredExpression,
  ScanProgress,
} from "./types";
import type { JSZipObject } from "jszip";

const MAX_CLASS_BYTES = 5 * 1024 * 1024;
const MAX_TEXT_BYTES = 2 * 1024 * 1024;
const MAX_HASHED_ENTRY_BYTES = 24 * 1024 * 1024;
export const MAX_ENTRY_HASH_BUDGET = 128 * 1024 * 1024;
/**
 * How many nested archives one scan will open. The walk spends it; the report
 * quotes it in the coverage caveat, so it is exported rather than retyped in
 * `analyzer.ts` -- the same single-owner rule as `MAX_ENTRY_HASH_BUDGET`.
 */
export const MAX_NESTED_ARCHIVES = 24;
const MAX_NESTED_ARCHIVE_BYTES = 32 * 1024 * 1024;
const MAX_NESTED_UNCOMPRESSED_BYTES = 96 * 1024 * 1024;
const MAX_BINARY_STRING_BYTES = 16 * 1024 * 1024;

export interface WalkInput {
  file: File;
  archive: JSZip;
  zipObjects: JSZipObject[];
  uploadedBytes: Uint8Array;
  analyzedBytes: Uint8Array;
  analyzedFileName: string;
  options: AnalyzeOptions;
  onProgress?: (progress: ScanProgress) => void;
  signal?: AbortSignal;
}

export interface WalkResult {
  sampleSha256: string;
  sampleSha512: string;
  sampleSha1: string;
  sampleMd5: string;
  analyzedSha256: string;
  knownHashMatches: KnownHashMatch[];
  /** Started during the walk, awaited by the reputation detector. */
  officialLookupPromise: Promise<OfficialFileMatch>;
  archiveEntries: ArchiveEntry[];
  archiveLayoutSha256: string;
  entryHashes: EntryHash[];
  /**
   * Decompressed entry bytes the walk already paid for. The payload inspector
   * and the YARA target builder both read it rather than inflating the same
   * entries again; this is the one cache that genuinely crosses phases.
   */
  hashedEntryBytes: Map<string, Uint8Array>;
  hashCandidates: ArchiveEntry[];
  topLevelHashedCount: number;
  contexts: DetectionContext[];
  classes: ClassInfo[];
  extractedStrings: ExtractedString[];
  dependencies: Dependency[];
  entryPoints: EntryPoint[];
  staticRecoveryBySource: Map<string, Map<string, number>>;
  staticRecoveredExpressions: RecoveredExpression[];
  remainingInstructionBudget: number;
  manifestRaw: string;
  manifest: Record<string, string>;
  /** Continues into the decoder, which indexes more strings than the walk. */
  stringId: number;
  classEntries: JSZipObject[];
  nestedArchivesInspected: number;
  nestedClassBudget: number;
  nestedCandidates: ArchiveEntry[];
  decodedValues: {
    xorBySource: Map<string, number>;
    characterPackedBySource: Map<string, number>;
  };
  decodedValueCount: number;
}

function expandEncodedValues(contexts: DetectionContext[]) {
  const seen = new Set<string>();
  let decodedCount = 0;
  const xorBySource = new Map<string, number>();
  const characterPackedBySource = new Map<string, number>();
  for (const context of contexts) {
    const additions: string[] = [];
    const addDecoded = (
      kind: string,
      decoded: string,
      count = true,
    ) => {
      const key = `${context.source}\u0000${kind}\u0000${decoded.toLowerCase()}`;
      if (seen.has(key) || additions.length >= 512) return;
      seen.add(key);
      additions.push(`[decoded ${kind}] ${decoded.slice(0, 4_096)}`);
      if (count) decodedCount += 1;
    };

    const xorFragments: Array<{ index: number; value: string }> = [];
    for (let index = 0; index < context.values.length; index += 1) {
      if (decodedCount >= 4_000 || additions.length >= 512) break;
      const decoded = decodeXorPackedString(context.values[index]);
      if (!decoded) continue;
      xorFragments.push({ index, value: decoded });
      addDecoded("xor-pack", decoded);
    }

    if (xorFragments.length) {
      xorBySource.set(context.source, xorFragments.length);
      additions.push(
        `[analysis marker] JARLAB_XOR_PACKED_STRINGS=${xorFragments.length}`,
      );
    }

    let characterPacked = 0;
    for (const value of context.values) {
      if (decodedCount >= 4_000 || additions.length >= 512) break;
      const decoded = decodeCharacterPackedString(value);
      if (!decoded) continue;
      characterPacked += 1;
      addDecoded("character-array packing", decoded);
    }
    if (characterPacked) {
      characterPackedBySource.set(context.source, characterPacked);
      additions.push(
        `[analysis marker] JARLAB_CHARACTER_PACKED_STRINGS=${characterPacked}`,
      );
    }

    for (
      let start = 0;
      start < xorFragments.length && additions.length < 512;
      start += 1
    ) {
      let combined = xorFragments[start].value;
      let reassembled: string | null = null;
      for (
        let end = start + 1;
        end < Math.min(xorFragments.length, start + 8);
        end += 1
      ) {
        if (xorFragments[end].index - xorFragments[end - 1].index > 16) break;
        combined += xorFragments[end].value;
        if (usefulReassembledValue(combined)) {
          reassembled = combined;
        }
      }
      if (reassembled) addDecoded("xor-reassembled", reassembled, false);
    }

    const decodeInputs = [...context.values, ...additions];
    for (const value of decodeInputs) {
      if (decodedCount >= 4_000 || additions.length >= 512) break;
      const base64Candidates =
        value.match(/[A-Za-z0-9+/_-]{20,4096}={0,2}/g) ?? [];
      for (const candidate of base64Candidates.slice(0, 8)) {
        const decoded = decodeBase64(candidate);
        if (
          decoded &&
          decoded !== candidate &&
          /[A-Za-z]{3}/.test(decoded)
        ) {
          addDecoded("base64", decoded);
        }
      }
      const hexCandidates = value.match(/\b[0-9a-fA-F]{16,4096}\b/g) ?? [];
      for (const candidate of hexCandidates.slice(0, 6)) {
        const decoded = decodeHex(candidate);
        if (
          decoded &&
          decoded !== candidate &&
          /[A-Za-z]{3}/.test(decoded)
        ) {
          addDecoded("hex", decoded);
        }
      }
    }
    context.values.push(...additions);
  }
  return { count: decodedCount, xorBySource, characterPackedBySource };
}

/**
 * How a string is classified for the string index. Shared with the detector
 * phase, which indexes the strings the symbolic engine recovers.
 */
export function stringCategory(value: string): ExtractedString["category"] {
  if (/https?:\/\/|java\.net|socket|webhook/i.test(value)) return "network";
  if (/token|password|secret|login data|local state|wallet/i.test(value))
    return "secret";
  if (/[A-Z]:\\|\/(?:home|tmp|etc|usr)\/|AppData|\.minecraft/i.test(value))
    return "path";
  if (/powershell|cmd\.exe|\/bin\/sh|ProcessBuilder|Runtime\.exec/i.test(value))
    return "command";
  if (/^[a-z_$][\w$]*(?:[./][a-z_$][\w$]*){2,}$/i.test(value))
    return "class";
  return "other";
}

export async function walkArchive(
  input: WalkInput,
): Promise<WalkResult> {
  const {
    file,
    archive,
    zipObjects,
    uploadedBytes,
    analyzedBytes,
    analyzedFileName,
    options,
    onProgress,
    signal,
  } = input;
reportProgress(
  onProgress,
  "Hashing sample",
  23,
  "Computing SHA-256, SHA-1, MD5, and archive fingerprints",
);
const sampleSha256 = digest("SHA-256", uploadedBytes);
const sampleSha512 = digest("SHA-512", uploadedBytes);
const sampleSha1 = digest("SHA-1", uploadedBytes);
const sampleMd5 = digest("MD5", uploadedBytes);
const analyzedSha256 =
  analyzedBytes === uploadedBytes
    ? sampleSha256
    : digest("SHA-256", analyzedBytes);
const analyzedSha512 =
  analyzedBytes === uploadedBytes
    ? sampleSha512
    : digest("SHA-512", analyzedBytes);
const analyzedSha1 =
  analyzedBytes === uploadedBytes
    ? sampleSha1
    : digest("SHA-1", analyzedBytes);
const analyzedMd5 =
  analyzedBytes === uploadedBytes ? sampleMd5 : digest("MD5", analyzedBytes);
const knownHashMatches: KnownHashMatch[] = [
  ...matchKnownHashes(
    { sha256: sampleSha256, sha1: sampleSha1, md5: sampleMd5 },
    "uploaded sample",
  ),
  ...(analyzedSha256 !== sampleSha256
    ? matchKnownHashes(
        {
          sha256: analyzedSha256,
          sha1: analyzedSha1,
          md5: analyzedMd5,
        },
        "analyzed archive",
        analyzedFileName,
      )
    : []),
];
const officialLookupPromise =
  options.officialLookup && /\.(?:jar|zip|mcpack|mrpack)$/i.test(file.name)
  ? lookupOfficialModrinthFile(
      analyzedSha512,
      analyzedFileName,
      signal,
    )
  : Promise.resolve(skippedOfficialLookup(analyzedSha512));
ensureNotAborted(signal);

const archiveEntries: ArchiveEntry[] = zipObjects
  .map((entry) => {
    const kind = classifyZipObject(entry);
    return {
      path: entry.name,
      size: zipSize(entry, "uncompressed"),
      compressedSize: zipSize(entry, "compressed"),
      kind,
      directory: entry.dir,
      suspicious:
        kind === "native" ||
        /(?:^|\/)(?:payload|stealer|grabber|inject|backdoor|rat)(?:[./_-]|$)/i.test(
          entry.name,
        ),
    };
  })
  .sort((left, right) => left.path.localeCompare(right.path));
const archiveLayoutSha256 = archiveLayoutDigest(archiveEntries);
const entryHashes: EntryHash[] = [];
const hashedEntryBytes = new Map<string, Uint8Array>();
let remainingEntryHashBudget = MAX_ENTRY_HASH_BUDGET;

const hashCandidates = archiveEntries
  .filter(
    (entry) =>
      !entry.directory &&
      entry.size <= MAX_HASHED_ENTRY_BYTES &&
      entry.size <= remainingEntryHashBudget,
  )
  .sort(
    (left, right) =>
      Number(
        right.suspicious ||
          right.kind === "archive" ||
          right.kind === "native" ||
          right.path.toLowerCase().endsWith(".dat"),
      ) -
        Number(
          left.suspicious ||
            left.kind === "archive" ||
            left.kind === "native" ||
            left.path.toLowerCase().endsWith(".dat"),
        ) ||
      left.size - right.size,
  );

for (let index = 0; index < hashCandidates.length; index += 1) {
  ensureNotAborted(signal);
  const entry = hashCandidates[index];
  if (
    remainingEntryHashBudget <= 0 ||
    entry.size > remainingEntryHashBudget ||
    entryHashes.length >= 4_000
  ) {
    break;
  }
  const zipEntry = archive.file(entry.path);
  if (!zipEntry) continue;
  try {
    const bytes = await zipEntry.async("uint8array");
    const entrySha256 = digest("SHA-256", bytes);
    const entrySha1 = digest("SHA-1", bytes);
    const entryMd5 = digest("MD5", bytes);
    entryHashes.push({
      path: entry.path,
      size: entry.size,
      kind: entry.kind,
      sha256: entrySha256,
      sha1: entrySha1,
      md5: entryMd5,
    });
    knownHashMatches.push(
      ...matchKnownHashes(
        {
          sha256: entrySha256,
          sha1: entrySha1,
          md5: entryMd5,
        },
        "archive entry",
        entry.path,
      ),
    );
    if (
      entry.kind === "archive" ||
      entry.kind === "native" ||
      entry.kind === "class" ||
      entry.kind === "script" ||
      entry.kind === "bytecode" ||
      entry.kind === "database" ||
      entry.kind === "text" ||
      entry.kind === "config" ||
      entry.path.toLowerCase().endsWith(".dat")
    ) {
      hashedEntryBytes.set(entry.path, bytes);
    }
    remainingEntryHashBudget -= bytes.byteLength;
  } catch {
    // A corrupt entry remains visible in the explorer and is skipped here.
  }
  if (index % 30 === 0) await nextFrame();
}
const topLevelHashedCount = entryHashes.length;

const contexts: DetectionContext[] = [];
const classes: ClassInfo[] = [];
const extractedStrings: ExtractedString[] = [];
const dependencies: Dependency[] = [];
const entryPoints: EntryPoint[] = [];
const staticRecoveryBySource = new Map<string, Map<string, number>>();
const staticRecoveredExpressions: RecoveredExpression[] = [];
let staticRecoveredCount = 0;
let remainingInstructionBudget = 60_000;
let manifestRaw = "";
let manifest: Record<string, string> = {};
let stringId = 0;

reportProgress(
  onProgress,
  "Indexing entries",
  30,
  `${archiveEntries.length.toLocaleString()} entries discovered`,
);

const classEntries = zipObjects.filter(
  (entry) =>
    !entry.dir &&
    classifyZipObject(entry) === "class" &&
    zipSize(entry, "uncompressed") <= MAX_CLASS_BYTES,
);
for (let index = 0; index < classEntries.length; index += 1) {
  ensureNotAborted(signal);
  const entry = classEntries[index];
  try {
    const bytes =
      hashedEntryBytes.get(entry.name) ??
      (await entry.async("uint8array"));
    const parsed = parseClassFile(bytes, entry.name);
    for (const method of parsed.methods) {
      if (method.instructions.length <= remainingInstructionBudget) {
        remainingInstructionBudget -= method.instructions.length;
        continue;
      }
      method.instructions = method.instructions.slice(
        0,
        Math.max(0, remainingInstructionBudget),
      );
      method.bytecodeTruncated =
        method.bytecodeTruncated || method.codeSize > 0;
      remainingInstructionBudget = 0;
    }
    classes.push(parsed);
    const staticRecovered = recoverStaticStrings(parsed);
    for (const item of staticRecovered) {
      staticRecoveredExpressions.push({
        id: `static-${staticRecoveredExpressions.length + 1}`,
        entry: entry.name,
        className: parsed.name,
        method: item.method ?? "(constant pool)",
        methodDescriptor: item.methodDescriptor,
        offset: item.offset ?? 0,
        sequenceStartOffset: item.sequenceStartOffset,
        value: item.value,
        expression: item.original.slice(0, 600),
        transform: staticRecoveredTransform(item.transform),
        confidence: "high",
        patchable: item.patchable,
      });
    }
    const values = [
      ...parsed.references,
      ...parsed.strings,
      ...staticRecovered.map(
        (item) =>
          `[decoded ${item.transform}${item.method ? ` @ ${item.method}` : ""}] ${item.value}`,
      ),
    ];
    contexts.push({ source: entry.name, values });
    if (staticRecovered.length) {
      staticRecoveredCount += staticRecovered.length;
      const counts = new Map<string, number>();
      for (const item of staticRecovered) {
        counts.set(item.transform, (counts.get(item.transform) ?? 0) + 1);
      }
      staticRecoveryBySource.set(entry.name, counts);
    }

    for (const value of parsed.strings) {
      if (decodeXorPackedString(value)) continue;
      const category = stringCategory(value);
      const flags = matchSuspiciousWords(value);
      const suspicious =
        category !== "class" && category !== "other" || flags.length > 0;
      if (
        extractedStrings.length < 4_000 &&
        (suspicious || value.length >= 8)
      ) {
        extractedStrings.push({
          id: `str-${++stringId}`,
          value,
          entry: entry.name,
          category,
          suspicious,
          flags,
        });
      }
    }
    for (const item of staticRecovered) {
      if (extractedStrings.length >= 4_000) break;
      const category = stringCategory(item.value);
      const flags = matchSuspiciousWords(item.value);
      extractedStrings.push({
        id: `str-${++stringId}`,
        value: item.value,
        entry: entry.name,
        category,
        suspicious:
          (category !== "class" && category !== "other") ||
          flags.length > 0 ||
          /initializeWeedhack|dev\.majanito|\/api\/delivery\/handler|\/files\/jar\/module|accessToken/i.test(
            item.value,
          ),
        flags,
        transform: item.transform,
        method: item.method,
      });
    }
  } catch {
    contexts.push({
      source: entry.name,
      values: [entry.name, "Unparseable or malformed class file"],
    });
  }

  if (index % 20 === 0) {
    reportProgress(
      onProgress,
      "Parsing classes",
      35 + Math.round((index / Math.max(1, classEntries.length)) * 27),
      `${index + 1} / ${classEntries.length} classes`,
    );
    await nextFrame();
  }
}

// The per-class pass above handles known encodings immediately. Once every
// class is indexed, this archive-wide pass can also evaluate internal pure
// helper methods and constant call chains across class boundaries.
const evaluatedExpressions = recoverConstantStringExpressions(classes);
const staticExpressionIndex = new Map(
  staticRecoveredExpressions.map((item, index) => [
    `${item.entry}\u0000${item.method}\u0000${item.offset}\u0000${item.value}`,
    index,
  ]),
);
const evaluatedByEntry = new Map<string, RecoveredExpression[]>();
for (const item of evaluatedExpressions) {
  const key = `${item.entry}\u0000${item.method}\u0000${item.offset}\u0000${item.value}`;
  const existingIndex = staticExpressionIndex.get(key);
  if (existingIndex !== undefined) {
    const existing = staticRecoveredExpressions[existingIndex];
    if (item.patchable && !existing.patchable) {
      staticRecoveredExpressions[existingIndex] = {
        ...existing,
        methodDescriptor: item.methodDescriptor,
        sequenceStartOffset: item.sequenceStartOffset,
        patchable: true,
      };
    }
    continue;
  }
  staticExpressionIndex.set(key, staticRecoveredExpressions.length);
  staticRecoveredExpressions.push(item);
  const values = evaluatedByEntry.get(item.entry) ?? [];
  values.push(item);
  evaluatedByEntry.set(item.entry, values);
}
for (const [entry, items] of evaluatedByEntry) {
  contexts.push({
    source: entry,
    values: items.map(
      (item) =>
        `[decoded bounded JVM evaluation @ ${item.method}] ${item.value}`,
    ),
  });
  staticRecoveredCount += items.length;
  const counts = staticRecoveryBySource.get(entry) ?? new Map<string, number>();
  counts.set(
    "bounded JVM evaluation",
    (counts.get("bounded JVM evaluation") ?? 0) + items.length,
  );
  staticRecoveryBySource.set(entry, counts);
  for (const item of items) {
    if (extractedStrings.length >= 4_000) break;
    const category = stringCategory(item.value);
    const flags = matchSuspiciousWords(item.value);
    extractedStrings.push({
      id: `str-${++stringId}`,
      value: item.value,
      entry,
      category,
      suspicious:
        (category !== "class" && category !== "other") || flags.length > 0,
      flags,
      transform: item.transform,
      method: item.method,
    });
  }
}

const textEntries = zipObjects.filter(
  (entry) =>
    !entry.dir &&
    (["manifest", "config", "text", "script"].includes(
      classifyZipObject(entry),
    ) ||
      entry.name.toLowerCase() === "meta-inf/manifest.mf" ||
      /^meta-inf\/services\/[^/]+$/i.test(entry.name) ||
      /meta-inf\/maven\/.+\/pom\.properties$/i.test(entry.name)) &&
    zipSize(entry, "uncompressed") <= MAX_TEXT_BYTES,
);
for (const entry of textEntries) {
  ensureNotAborted(signal);
  try {
    const cached = hashedEntryBytes.get(entry.name);
    const raw = cached
      ? new TextDecoder("utf-8", { fatal: false }).decode(cached)
      : await entry.async("text");
    contexts.push({
      source: entry.name,
      values: [entry.name, ...raw.split(/\r?\n/).slice(0, 10_000)],
    });
    if (entry.name.toLowerCase() === "meta-inf/manifest.mf") {
      manifestRaw = raw;
      manifest = parseManifest(raw);
    }
    if (entry.name.toLowerCase().endsWith("fabric.mod.json")) {
      try {
        const fabric = JSON.parse(raw) as {
          entrypoints?: Record<
            string,
            Array<string | { value?: string }>
          >;
        };
        for (const [environment, values] of Object.entries(
          fabric.entrypoints ?? {},
        )) {
          for (const value of values) {
            const rawClass =
              typeof value === "string" ? value : (value.value ?? "");
            const className = rawClass.split("::")[0].trim();
            if (className) {
              entryPoints.push({
                type: "fabric",
                className,
                source: entry.name,
                environment,
              });
            }
          }
        }
      } catch {
        // Malformed metadata remains available in the archive explorer.
      }
    }
    if (
      /(?:^|\/)(?:plugin|paper-plugin|bungee)\.ya?ml$/i.test(entry.name)
    ) {
      const main = raw.match(/^\s*main\s*:\s*["']?([^"'#\s]+)["']?/im)?.[1];
      if (main) {
        entryPoints.push({
          type: /paper-plugin/i.test(entry.name)
            ? "paper"
            : /bungee/i.test(entry.name)
              ? "other"
              : "bukkit",
          className: main,
          source: entry.name,
        });
      }
    }
    if (entry.name.toLowerCase().endsWith("velocity-plugin.json")) {
      try {
        const velocity = JSON.parse(raw) as { main?: string };
        if (velocity.main) {
          entryPoints.push({
            type: "velocity",
            className: velocity.main,
            source: entry.name,
          });
        }
      } catch {
        // Malformed metadata remains available in the archive explorer.
      }
    }
    if (/^meta-inf\/services\/[^/]+$/i.test(entry.name)) {
      for (const className of raw
        .split(/\r?\n/)
        .map((line) => line.replace(/#.*/, "").trim())
        .filter(Boolean)
        .slice(0, 100)) {
        entryPoints.push({
          type: "service",
          className,
          source: entry.name,
        });
      }
    }
    if (/meta-inf\/maven\/.+\/pom\.properties$/i.test(entry.name)) {
      const props = parseProperties(raw);
      const pathParts = entry.name.split("/");
      const dependency: Dependency = {
        groupId: props.groupId || pathParts.at(-3) || "unknown",
        artifactId: props.artifactId || pathParts.at(-2) || "unknown",
        version: props.version || "unknown",
        source: entry.name,
      };
      // Recorded because the fallbacks above are inferences from Maven's
      // directory layout, not statements from the file. Downstream exports
      // have to be able to tell the two apart.
      dependency.origins = {
        groupId: props.groupId
          ? "parsed"
          : pathParts.at(-3)
            ? "path"
            : "missing",
        artifactId: props.artifactId
          ? "parsed"
          : pathParts.at(-2)
            ? "path"
            : "missing",
        version: props.version ? "parsed" : "missing",
      };
      dependencies.push(dependency);
    }
  } catch {
    // Corrupt text resources stay visible in the explorer.
  }
}

for (const entry of archiveEntries) {
  if (
    entry.directory ||
    entry.size > MAX_BINARY_STRING_BYTES ||
    !(
      entry.kind === "native" ||
      entry.path.toLowerCase().endsWith(".dat") ||
      (entry.kind === "binary" && entry.suspicious)
    )
  ) {
    continue;
  }
  const bytes = hashedEntryBytes.get(entry.path);
  if (!bytes) continue;
  const values = extractBinaryStrings(bytes);
  if (values.length) {
    contexts.push({
      source: entry.path,
      values: [entry.path, ...values],
    });
  }
}

let nestedArchivesInspected = 0;
let nestedUncompressedBudget = MAX_NESTED_UNCOMPRESSED_BYTES;
let nestedClassBudget = 2_000;
// The full candidate list, deliberately *not* sliced here. This used to be
// `.slice(0, MAX_NESTED_ARCHIVES)` at this point, which meant the report's
// "N nested archives were malformed" caveat compared a truncated list against
// the count actually inspected and could therefore never fire because of the
// cap: an outer jar with more archives than the cap silently lost the
// remainder. The cap is applied at the loop instead, so the candidate count
// the caveat reads is the truth about the archive.
const nestedCandidates = archiveEntries.filter(
  (entry) =>
    !entry.directory &&
    entry.kind === "archive" &&
    entry.size <= MAX_NESTED_ARCHIVE_BYTES,
);

for (const nestedEntry of nestedCandidates.slice(0, MAX_NESTED_ARCHIVES)) {
  ensureNotAborted(signal);
  const nestedBytes =
    hashedEntryBytes.get(nestedEntry.path) ??
    (await archive.file(nestedEntry.path)?.async("uint8array"));
  if (!nestedBytes || nestedBytes.byteLength > MAX_NESTED_ARCHIVE_BYTES)
    continue;
  try {
    const nestedArchive = await JSZip.loadAsync(nestedBytes, {
      checkCRC32: false,
      createFolders: true,
    });
    const nestedObjects = Object.values(nestedArchive.files);
    const nestedExpanded = nestedObjects.reduce(
      (total, item) => total + zipSize(item, "uncompressed"),
      0,
    );
    if (
      nestedObjects.length > MAX_ENTRIES ||
      nestedExpanded > nestedUncompressedBudget
    ) {
      contexts.push({
        source: nestedEntry.path,
        values: [
          nestedEntry.path,
          `Nested archive skipped: ${nestedObjects.length} entries, ${nestedExpanded} expanded bytes`,
        ],
      });
      continue;
    }

    nestedArchivesInspected += 1;
    nestedUncompressedBudget -= nestedExpanded;
    for (const inner of nestedObjects) {
      if (inner.dir) continue;
      const source = `${nestedEntry.path}!/${inner.name}`;
      const innerSize = zipSize(inner, "uncompressed");
      contexts.push({ source, values: [source, inner.name] });

      if (
        innerSize <= MAX_HASHED_ENTRY_BYTES &&
        entryHashes.length < 4_000 &&
        remainingEntryHashBudget > 0 &&
        innerSize <= remainingEntryHashBudget
      ) {
        try {
          const bytes = await inner.async("uint8array");
          const innerSha256 = digest("SHA-256", bytes);
          const innerSha1 = digest("SHA-1", bytes);
          const innerMd5 = digest("MD5", bytes);
          const kind = classifyEntry(inner.name, false);
          entryHashes.push({
            path: source,
            size: innerSize,
            kind,
            sha256: innerSha256,
            sha1: innerSha1,
            md5: innerMd5,
          });
          knownHashMatches.push(
            ...matchKnownHashes(
              {
                sha256: innerSha256,
                sha1: innerSha1,
                md5: innerMd5,
              },
              "archive entry",
              source,
            ),
          );
          remainingEntryHashBudget -= bytes.byteLength;

          if (
            kind === "class" &&
            innerSize <= MAX_CLASS_BYTES &&
            nestedClassBudget > 0
          ) {
            try {
              const parsed = parseClassFile(bytes, source);
              const staticRecovered = recoverStaticStrings(parsed);
              for (const item of staticRecovered) {
                staticRecoveredExpressions.push({
                  id: `static-${staticRecoveredExpressions.length + 1}`,
                  entry: source,
                  className: parsed.name,
                  method: item.method ?? "(constant pool)",
                  offset: item.offset ?? 0,
                  value: item.value,
                  expression: item.original.slice(0, 600),
                  transform: staticRecoveredTransform(item.transform),
                  confidence: "high",
                });
              }
              contexts.push({
                source,
                values: [
                  source,
                  ...parsed.references,
                  ...parsed.strings,
                  ...staticRecovered.map(
                    (item) =>
                      `[decoded ${item.transform}${item.method ? ` @ ${item.method}` : ""}] ${item.value}`,
                  ),
                ],
              });
              if (staticRecovered.length) {
                staticRecoveredCount += staticRecovered.length;
                const counts = new Map<string, number>();
                for (const item of staticRecovered) {
                  counts.set(
                    item.transform,
                    (counts.get(item.transform) ?? 0) + 1,
                  );
                  if (extractedStrings.length < 4_000) {
                    const category = stringCategory(item.value);
                    const flags = matchSuspiciousWords(item.value);
                    extractedStrings.push({
                      id: `str-${++stringId}`,
                      value: item.value,
                      entry: source,
                      category,
                      suspicious:
                        (category !== "class" && category !== "other") ||
                        flags.length > 0 ||
                        /initializeWeedhack|dev\.majanito|\/api\/delivery\/handler|\/files\/jar\/module|accessToken/i.test(
                          item.value,
                        ),
                      flags,
                      transform: item.transform,
                      method: item.method,
                    });
                  }
                }
                staticRecoveryBySource.set(source, counts);
              }
              nestedClassBudget -= 1;
            } catch {
              // The entry path and hash still remain available as evidence.
            }
          } else if (
            (kind === "text" ||
              kind === "config" ||
              kind === "script" ||
              kind === "manifest") &&
            innerSize <= MAX_TEXT_BYTES
          ) {
            const raw = new TextDecoder("utf-8", { fatal: false }).decode(
              bytes,
            );
            contexts.push({
              source,
              values: [source, ...raw.split(/\r?\n/).slice(0, 10_000)],
            });
          } else if (
            (kind === "native" ||
              inner.name.toLowerCase().endsWith(".dat")) &&
            innerSize <= MAX_BINARY_STRING_BYTES
          ) {
            contexts.push({
              source,
              values: [source, ...extractBinaryStrings(bytes)],
            });
          }
        } catch {
          // Keep scanning sibling entries in a partially corrupt nested JAR.
        }
      }
    }
  } catch {
    contexts.push({
      source: nestedEntry.path,
      values: [nestedEntry.path, "Malformed nested archive"],
    });
  }
  await nextFrame();
}

for (const entry of archiveEntries) {
  contexts.push({ source: entry.path, values: [entry.path] });
}

const decodedValues = expandEncodedValues(contexts);
const decodedValueCount = decodedValues.count + staticRecoveredCount;
for (const context of contexts) {
  for (const value of context.values) {
    if (
      !value.startsWith("[decoded ") ||
      extractedStrings.length >= 4_000
    ) {
      continue;
    }
    const decoded = value.match(
      /^\[decoded ([^@\]]+?)(?: @ ([^\]]+))?]\s*([\s\S]*)$/,
    );
    const cleanValue = decoded?.[3] ?? value;
    const category = stringCategory(cleanValue);
    const flags = matchSuspiciousWords(cleanValue);
    extractedStrings.push({
      id: `str-${++stringId}`,
      value: cleanValue,
      entry: context.source,
      category,
      suspicious:
        category !== "class" && category !== "other" || flags.length > 0,
      flags,
      transform: decoded?.[1]?.trim(),
      method: decoded?.[2]?.trim(),
    });
  }
}

const extractedStringKeys = new Set(
  extractedStrings.map(
    (item) =>
      `${item.entry}\u0000${item.method ?? ""}\u0000${item.value}`,
  ),
);
for (const context of contexts) {
  for (const value of context.values) {
    if (extractedStrings.length >= 4_000) break;
    const decoded = value.match(
      /^\[decoded ([^@\]]+?)(?: @ ([^\]]+))?]\s*([\s\S]*)$/,
    );
    const cleanValue = decoded?.[3] ?? value;
    const flags = matchSuspiciousWords(cleanValue);
    if (!flags.length) continue;
    const method = decoded?.[2]?.trim();
    const key = `${context.source}\u0000${method ?? ""}\u0000${cleanValue}`;
    if (extractedStringKeys.has(key)) continue;
    extractedStringKeys.add(key);
    extractedStrings.push({
      id: `str-${++stringId}`,
      value: cleanValue,
      entry: context.source,
      category: stringCategory(cleanValue),
      suspicious: true,
      flags,
      transform: decoded?.[1]?.trim(),
      method,
    });
  }
}

for (const [attribute, type] of [
  ["Main-Class", "main"],
  ["Premain-Class", "other"],
  ["Agent-Class", "other"],
] as const) {
  const className = manifest[attribute];
  if (className) {
    entryPoints.push({
      type,
      className,
      source: "META-INF/MANIFEST.MF",
    });
  }
}

  return {
    sampleSha256,
    sampleSha512,
    sampleSha1,
    sampleMd5,
    analyzedSha256,
    knownHashMatches,
    officialLookupPromise,
    archiveEntries,
    archiveLayoutSha256,
    entryHashes,
    hashedEntryBytes,
    hashCandidates,
    topLevelHashedCount,
    contexts,
    classes,
    extractedStrings,
    dependencies,
    entryPoints,
    staticRecoveryBySource,
    staticRecoveredExpressions,
    remainingInstructionBudget,
    manifestRaw,
    manifest,
    stringId,
    classEntries,
    nestedArchivesInspected,
    nestedClassBudget,
    nestedCandidates,
    decodedValues,
    decodedValueCount,
  };
}
